/**
 * Enhanced Shipment Costing Data Model
 * Includes VAT breakdown fields for fuel and maintenance costs
 */

/**
 * @typedef {Object} VATBreakdown
 * @property {number} baseAmount - Amount before VAT
 * @property {number} vatAmount - VAT amount (15%)
 * @property {number} totalAmount - Total amount (base + VAT)
 * @property {boolean} includesVAT - Whether the entered amount included VAT
 */

/**
 * @typedef {Object} CostEntry
 * @property {string} description - Description of the cost
 * @property {number} quantity - Quantity (for stops)
 * @property {number} amount - Amount entered by user
 */

/**
 * @typedef {Object} EnhancedShipmentCosting
 * @property {string} id - Document ID
 * @property {string} shipmentId - Reference to shipment
 * @property {string} companyId - Company ID
 * @property {string} driverId - Driver ID (optional)
 * @property {string} createdBy - User who created the costing
 * @property {Date} createdAt - Creation timestamp
 * @property {Date} updatedAt - Last update timestamp
 * 
 * // Legacy fields (for backward compatibility)
 * @property {number} fuel - Original fuel amount (deprecated)
 * @property {number} maintenance - Original maintenance amount (deprecated)
 * @property {number} commission - Commission amount (VAT-exempt)
 * 
 * // Manual Fuel Costs (user-entered)
 * @property {number} manualFuelAmount - User-entered manual fuel amount
 * @property {boolean} manualFuelIncludesVAT - Whether manual fuel amount includes VAT
 * @property {number} manualFuelBaseAmount - Calculated manual fuel base amount
 * @property {number} manualFuelVATAmount - Calculated manual fuel VAT amount
 * 
 * // PetroApp Fuel Costs (imported from Excel)
 * @property {number} petroAppFuelAmount - Total PetroApp fuel amount
 * @property {boolean} petroAppFuelIncludesVAT - Whether PetroApp fuel includes VAT (usually true)
 * @property {number} petroAppFuelBaseAmount - Calculated PetroApp fuel base amount
 * @property {number} petroAppFuelVATAmount - Calculated PetroApp fuel VAT amount
 * @property {Array} appliedFuelTransactions - Array of applied PetroApp fuel transactions
 * 
 * // Combined Fuel Totals (calculated fields)
 * @property {number} totalFuelAmount - Combined manual + PetroApp fuel amount
 * @property {number} totalFuelBaseAmount - Combined fuel base amount
 * @property {number} totalFuelVATAmount - Combined fuel VAT amount
 * 
 * // Legacy combined field (for backward compatibility)
 * @property {number} fuelAmount - Combined fuel amount (deprecated, use totalFuelAmount)
 * @property {boolean} fuelIncludesVAT - Whether fuel amount includes VAT (deprecated)
 * @property {number} fuelBaseAmount - Calculated fuel base amount (deprecated)
 * @property {number} fuelVATAmount - Calculated fuel VAT amount (deprecated)
 * 
 * @property {number} maintenanceAmount - User-entered maintenance amount
 * @property {boolean} maintenanceIncludesVAT - Whether maintenance amount includes VAT
 * @property {number} maintenanceBaseAmount - Calculated maintenance base amount
 * @property {number} maintenanceVATAmount - Calculated maintenance VAT amount
 * 
 * // Commission remains VAT-exempt as per Saudi regulations
 * @property {number} commissionAmount - Commission amount (no VAT)
 * 
 * // Other costs and stops (unchanged)
 * @property {CostEntry[]} stops - Stop costs
 * @property {CostEntry[]} otherCosts - Other miscellaneous costs
 * 
 * // Status and metadata
 * @property {string} costingStatus - Status: 'not_added', 'partial', 'completed'
 * @property {string} notes - Optional notes
 * 
 * // VAT calculation metadata
 * @property {string} vatCalculationVersion - Version for migration tracking
 * @property {Date} vatCalculatedAt - When VAT was last calculated
 * @property {string} vatCalculatedBy - Who calculated the VAT
 */

/**
 * Default costing data structure
 */
export const DEFAULT_COSTING_DATA = {
  // Manual Fuel (user-entered)
  manualFuelAmount: 0,
  manualFuelIncludesVAT: true, // Default to VAT-inclusive
  manualFuelBaseAmount: 0,
  manualFuelVATAmount: 0,
  
  // PetroApp Fuel (imported)
  petroAppFuelAmount: 0,
  petroAppFuelIncludesVAT: true, // PetroApp prices usually include VAT
  petroAppFuelBaseAmount: 0,
  petroAppFuelVATAmount: 0,
  appliedFuelTransactions: [], // Track applied PetroApp transactions
  
  // Combined Fuel Totals (calculated)
  totalFuelAmount: 0,
  totalFuelBaseAmount: 0,
  totalFuelVATAmount: 0,
  
  // Maintenance
  maintenanceAmount: 0,
  maintenanceIncludesVAT: true, // Default to VAT-inclusive
  maintenanceBaseAmount: 0,
  maintenanceVATAmount: 0,
  
  // Commission (VAT-exempt)
  commissionAmount: 0,
  
  // Other costs (unchanged)
  stops: [],
  otherCosts: [],
  
  // Status and metadata
  costingStatus: 'not_added',
  notes: '',
  
  // VAT calculation metadata
  vatCalculationVersion: '2.0', // Updated version for new structure
  vatCalculatedAt: null,
  vatCalculatedBy: null,
  
  // Legacy fields (for backward compatibility)
  fuel: 0,
  maintenance: 0,
  commission: 0,
  fuelAmount: 0,
  fuelIncludesVAT: true,
  fuelBaseAmount: 0,
  fuelVATAmount: 0
};

/**
 * Validation rules for costing data
 */
export const COSTING_VALIDATION_RULES = {
  fuelAmount: {
    min: 0,
    max: 999999,
    required: false
  },
  maintenanceAmount: {
    min: 0,
    max: 999999,
    required: false
  },
  commissionAmount: {
    min: 0,
    max: 999999,
    required: false
  },
  vatCalculation: {
    tolerance: 0.01 // Tolerance for floating point calculations
  }
};

/**
 * Status options for costing
 */
export const COSTING_STATUS_OPTIONS = {
  NOT_ADDED: 'not_added',
  PARTIAL: 'partial',
  COMPLETED: 'completed'
};

/**
 * VAT rate for Saudi Arabia
 */
export const SAUDI_VAT_RATE = 0.15;